import React from "react";
import { Plus, Trash2 } from "lucide-react";

// Item type options. "Other (Specify)" reveals a free-text field per row.
// Keep in sync with the copies previously hardcoded in CreateJob.jsx / JobStatus.jsx.
export const ITEM_TYPES = ["Pallet", "Crate", "Box", "IBC", "Carton", "CHEP"];
export const ITEM_OTHER = "Other (Specify)";

const emptyRow = () => ({ id: crypto.randomUUID?.() || String(Math.random()), type: "", qty: 1, other: "" });

// Backend still stores a single `item_type` string + `item_qty` number (see
// backend/server.py) — rows are a client-side-only convenience so a job can
// carry "5 x Pallet, 3 x Carton, 3 x CHEP" without a schema change. This
// parses that stored string back into editable rows, and also round-trips
// jobs created before multi-select existed (a plain "Pallet" + item_qty 5).
export function parseItemRows(itemType, itemQty) {
  const raw = (itemType || "").trim();
  if (!raw) return [emptyRow()];
  const segments = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (segments.length === 0) return [emptyRow()];
  const rows = segments.map((seg) => {
    const m = seg.match(/^(\d+(?:\.\d+)?)\s*x\s*(.+)$/i);
    const type = (m ? m[2] : seg).trim();
    const qty = m ? m[1] : (itemQty || 1);
    return ITEM_TYPES.includes(type)
      ? { ...emptyRow(), type, qty }
      : { ...emptyRow(), type: ITEM_OTHER, qty, other: type };
  });
  return rows;
}

// Combines rows back into the single item_type string + summed item_qty the
// backend expects.
export function deriveItemFields(rows) {
  const active = (rows || []).filter((r) => (r.type === ITEM_OTHER ? r.other.trim() : r.type) && Number(r.qty) > 0);
  const item_type = active
    .map((r) => `${Number(r.qty)} x ${r.type === ITEM_OTHER ? r.other.trim() : r.type}`)
    .join(", ");
  const item_qty = active.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
  return { item_type, item_qty };
}

export { emptyRow as emptyItemRow };

// Multiple simultaneous item-type + qty rows (e.g. 5 Pallets AND 3 Cartons AND
// 3 CHEPs on one job) instead of a single dropdown that only holds one type.
export default function ItemMultiSelect({ rows, onRowsChange, disabled }) {
  const updateRow = (id, patch) => onRowsChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => onRowsChange([...rows, emptyRow()]);
  const removeRow = (id) => onRowsChange(rows.length > 1 ? rows.filter((r) => r.id !== id) : rows);

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.id} className="space-y-1.5">
          <div className="flex gap-2">
            <select
              disabled={disabled}
              value={row.type}
              onChange={(e) => updateRow(row.id, { type: e.target.value })}
              className="df-input-rect"
              data-testid="item-type"
            >
              <option value="" className="bg-[#15151f]">Select Item</option>
              {[...ITEM_TYPES, ITEM_OTHER].map((o) => <option key={o} value={o} className="bg-[#15151f]">{o}</option>)}
            </select>
            <input
              type="number"
              min="1"
              disabled={disabled}
              value={row.qty}
              onChange={(e) => updateRow(row.id, { qty: e.target.value })}
              className="df-input-rect w-20"
              data-testid="item-qty"
            />
            {rows.length > 1 && !disabled && (
              <button
                type="button"
                onClick={() => removeRow(row.id)}
                className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 shrink-0"
                title="Remove item"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          {row.type === ITEM_OTHER && (
            <input
              disabled={disabled}
              value={row.other}
              onChange={(e) => updateRow(row.id, { other: e.target.value })}
              placeholder="Specify item type"
              className="df-input-rect"
            />
          )}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-lg"
          style={{ background: "rgba(25,230,200,0.15)", color: "#19e6c8" }}
        >
          <Plus size={12} /> Add Item
        </button>
      )}
    </div>
  );
}
